import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import formatters from './formatters';
import transformers from './transformers';
import { FormattedUnitLabels, UnitValue } from './unit-formatter.types';
import { unitValueToFormattedUnitLabels } from './unit-formatter.utils';

/**
 * Formats a unit value to a formatted unit labels object.
 * @param unitValue - The unit value to format.
 * @param displayUnits - The display units to transform and format the unit value to.
 * @returns The formatted unit labels object.
 */
export const formatUnits = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  if (unitValue.units === '') {
    unitValue.units = 'none';
  }

  const formatter = formatters[displayUnits?.toLowerCase() ?? unitValue.units?.toLowerCase() ?? ''];
  if (formatter) {
    return formatter(unitValue, displayUnits);
  }

  const outputUnitValue = transformUnits(unitValue, displayUnits);

  return unitValueToFormattedUnitLabels({
    value: outputUnitValue.value,
    units: outputUnitValue.units,
  });
};

/**
 * Transforms a unit value to a new unit value.
 * @param unitValue - The unit value to transform.
 * @param newUnits - The new units to transform the unit value to.
 * @returns The transformed unit value.
 */
export const transformUnits = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  // If the units is undefined, empty, set units to 'none'
  // This is to ensure that the none transformer is run
  if (isNullOrUndefined(unitValue.units) || unitValue.units === '') {
    unitValue.units = 'none';
  }

  const transformer = transformers[unitValue.units?.toLowerCase() ?? ''];

  if (transformer) {
    return transformer(unitValue, newUnits);
  }

  return unitValue;
};
